import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { motion } from "framer-motion";
import { useSearchParams } from "wouter";
import { Check } from "lucide-react";
import { useQuizQuestions } from "@/data/quiz";
import { useUpdateProfile, useProfile, useInvalidateProfile } from "@/lib/api";
import QuestionCard from "../components/quiz/QuestionCard";
import Interstitial from "../components/quiz/Interstitial";
import ScanningScreen from "../components/quiz/ScanningScreen";
import { useToast } from "@/components/ux/Toast";
import { useAuth } from "@/lib/platform/AuthProvider";
import { useAppNavigation } from "@/lib/platform";
import tracking from "@/lib/tracking";
import { getStoredFacebookAttribution } from "@/lib/tracking/attribution";
import { getStoredAdEventType } from "@/lib/quizEventType";
import { resolvePostQuizDestination } from "@/lib/postSignupNav";
import cx from "classnames";
import { useGuidedPostSignupBooking } from "@/lib/features";
import { useT } from "@/lib/i18n";
import { Loader } from '@/components/Loader';

export default function Quiz() {
  const { t } = useT();
  const guidedBookingEnabled = useGuidedPostSignupBooking();
  const quizQuestions = useQuizQuestions();
  const { isLoaded: isUserLoaded, isSignedIn, openSignIn } = useAuth();
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [isInitialized, setIsInitialized] = useState(false);
  const { navigate } = useAppNavigation();
  const [searchParams] = useSearchParams();
  const shouldSaveAnswers = useRef<boolean>(
    searchParams.get("saveAnswers") != null,
  );
  console.log('shouldSaveAnswers', shouldSaveAnswers.current, searchParams.get('saveAnswers') != null, window.location.href);
  const [isSaving, setIsSaving] = useState(false);
  const [showScanning, setShowScanning] = useState(false);

  useEffect(() => {
    if (isSaving) {
      return;
    }
    if (searchParams.get('saveAnswers') == null) {
      return;
    }
    if (shouldSaveAnswers.current) {
      return;
    }
    shouldSaveAnswers.current = true;
  }, [isSaving, searchParams]);

  const updateProfile = useUpdateProfile();
  const invalidateProfile = useInvalidateProfile();
  const { data: profileData, isLoading: isLoadingProfile } = useProfile();
  const toast = useToast();

  // Check if user has already completed the quiz (returning user editing profile)
  const hasCompletedQuizBefore = useMemo(() => {
    return !!profileData?.data?.user?.quizCompletedAt;
  }, [profileData]);

  // Interstitials are for first-time takers, not returning users editing answers
  const interstitialsEnabled = !hasCompletedQuizBefore;

  const [shownInterstitials, setShownInterstitials] = useState<
    Record<string, { questionId: string; answer: string | string[] | number }>
  >({});
  // Ref map to store refs for each question card
  const questionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Load saved progress from profile data, localStorage
  useEffect(() => {
    if (isLoadingProfile || isInitialized || !isUserLoaded) return;

    tracking.quizStarted();

    let initialAnswers: Record<string, any> = {};
    // First, load from profile if it exists
    if (profileData?.success && profileData?.data?.profile) {
      const profile = profileData.data.profile;
      initialAnswers = {
        location: profile.location,
        dinnerVibe: profile.dinnerVibe,
        socialBattery: profile.socialBattery,
        groupRole: profile.groupRole,
        communicationStyle: profile.communicationStyle,
        groupChatRole: profile.groupChatRole,
        humorStyle: profile.humorStyle,
        careerStage: profile.careerStage,
        industry: profile.industry,
        unwindActivities: profile.unwindActivities,
        weekendEnergy: profile.weekendEnergy,
        conversationTopics: profile.conversationTopics,
        hobbies: profile.hobbies,
        relationshipStatus: profile.relationshipStatus,
        genderIdentity: profile.genderIdentity,
        pronouns: profile.pronouns,
        sexualOrientation: profile.sexualOrientation,
        birthday: profile.birthday,
        primaryValue1: profile.primaryValue1,
        primaryValue2: profile.primaryValue2,
        conflictStyle: profile.conflictStyle,
        feedbackStyle: profile.feedbackStyle,
        communityPreference: profile.communityPreference,
        politicalViews: profile.politicalViews,
        topicsOpenTo: profile.topicsOpenTo,
        phone: profileData.data.user.phone,
        instagram: profile.instagram,
      };
      // Filter out undefined values
      Object.keys(initialAnswers).forEach((key) => {
        if (initialAnswers[key] === undefined || initialAnswers[key] === null) {
          delete initialAnswers[key];
        }
      });
    }

    // Then override with localStorage if it exists (draft takes precedence)
    const savedAnswers = localStorage.getItem("quizAnswers");
    if (savedAnswers) {
      const parsedAnswers = JSON.parse(savedAnswers);
      initialAnswers = { ...initialAnswers, ...parsedAnswers };
    }

    if (Object.keys(initialAnswers).length > 0) {
      setAnswers(initialAnswers);
    }

    setIsInitialized(true);
  }, [profileData, isLoadingProfile, isInitialized, isUserLoaded]);

  // Save progress to localStorage
  useEffect(() => {
    if (Object.keys(answers).length > 0) {
      localStorage.setItem("quizAnswers", JSON.stringify(answers));
    }
  }, [answers]);

  // Helper: check if a question has any answer
  const isQuestionAnswered = useCallback(
    (questionId: string) => {
      const answer = answers[questionId];
      return (
        answer !== undefined &&
        answer !== "" &&
        (!Array.isArray(answer) || answer.length > 0)
      );
    },
    [answers],
  );

  // SIMPLE VISIBILITY: Question N shows if question N-1 has any answer (or N is first).
  // Optional questions don't gate the next one — skipping them still reveals what follows.
  // Returning users (quiz already completed) see all questions immediately.
  const getVisibleQuestionIndices = useCallback(() => {
    const visible = new Set<number>();

    if (hasCompletedQuizBefore) {
      for (let i = 0; i < quizQuestions.length; i++) visible.add(i);
      return visible;
    }

    for (let i = 0; i < quizQuestions.length; i++) {
      // First question is always visible
      if (i === 0) {
        visible.add(i);
        continue;
      }
      const prevQ = quizQuestions[i - 1];
      if (isQuestionAnswered(prevQ.id) || !prevQ.required) {
        visible.add(i);
      }
    }
    return visible;
  }, [isQuestionAnswered, quizQuestions, hasCompletedQuizBefore]);

  const visibleIndices = getVisibleQuestionIndices();

  // Find the first unanswered required question (for preview blur)
  const firstUnansweredIndex = quizQuestions.findIndex(
    (q) => q.required && !isQuestionAnswered(q.id),
  );

  // Preview (blurred) is one after the first unanswered.
  // Skip the preview blur for returning users — they should see everything.
  const previewQuestionIndex =
    !hasCompletedQuizBefore &&
    firstUnansweredIndex !== -1 &&
    firstUnansweredIndex < quizQuestions.length - 1
      ? firstUnansweredIndex + 1
      : -1;

  // Group questions by category
  const questionsByCategory = quizQuestions.reduce(
    (acc, q, index) => {
      if (!acc[q.category!]) {
        acc[q.category!] = [];
      }
      acc[q.category!].push({ ...q, index });
      return acc;
    },
    {} as Record<string, Array<(typeof quizQuestions)[0] & { index: number }>>,
  );

  // Check if all required questions are answered
  const allRequiredAnswered = quizQuestions
    .filter((q) => q.required)
    .every((q) => isQuestionAnswered(q.id)) || hasCompletedQuizBefore;

  // Calculate overall progress
  const answeredCount = quizQuestions.filter(
    (q) => q.required && isQuestionAnswered(q.id),
  ).length;
  const totalCount = quizQuestions.filter((q) => q.required).length;
  const progress = Math.round((answeredCount / totalCount) * 100);

  // Get saved answers from profile data
  const savedAnswers = useMemo((): Record<string, any> => {
    if (!profileData?.success || !profileData?.data?.profile) {
      return {};
    }
    const profile = profileData.data.profile;
    return {
      location: profile.location,
      dinnerVibe: profile.dinnerVibe,
      groupRole: profile.groupRole,
      communicationStyle: profile.communicationStyle,
      groupChatRole: profile.groupChatRole,
      humorStyle: profile.humorStyle,
      careerStage: profile.careerStage,
      industry: profile.industry,
      unwindActivities: profile.unwindActivities,
      weekendEnergy: profile.weekendEnergy,
      conversationTopics: profile.conversationTopics,
      hobbies: profile.hobbies,
      socialBattery: profile.socialBattery,
      relationshipStatus: profile.relationshipStatus,
      genderIdentity: profile.genderIdentity,
      pronouns: profile.pronouns,
      sexualOrientation: profile.sexualOrientation,
      birthday: profile.birthday,
      primaryValue1: profile.primaryValue1,
      primaryValue2: profile.primaryValue2,
      conflictStyle: profile.conflictStyle,
      feedbackStyle: profile.feedbackStyle,
      communityPreference: profile.communityPreference,
      politicalViews: profile.politicalViews,
      topicsOpenTo: profile.topicsOpenTo,
      phone: profileData.data.user.phone,
      instagram: profile.instagram,
    };
  }, [profileData]);

  // Check if a specific question's answer has changed from saved
  const isQuestionChanged = useCallback(
    (questionId: string) => {
      const currentValue = answers[questionId];
      const savedValue = savedAnswers[questionId];

      // If no saved value existed and current is empty, not changed
      const currentEmpty =
        currentValue === "" ||
        currentValue === undefined ||
        currentValue === null ||
        (Array.isArray(currentValue) && currentValue.length === 0);
      const savedEmpty =
        savedValue === "" ||
        savedValue === undefined ||
        savedValue === null ||
        (Array.isArray(savedValue) && savedValue.length === 0);

      if (currentEmpty && savedEmpty) return false;

      // Handle arrays
      if (Array.isArray(currentValue) || Array.isArray(savedValue)) {
        const currentArr = Array.isArray(currentValue) ? currentValue : [];
        const savedArr = Array.isArray(savedValue) ? savedValue : [];
        if (currentArr.length !== savedArr.length) return true;
        return !currentArr.every((v) => savedArr.includes(v));
      }

      return currentValue !== savedValue;
    },
    [answers, savedAnswers],
  );

  // Check if there are unsaved changes compared to profile data
  const hasUnsavedChanges = useMemo(() => {
    if (!profileData?.success || !profileData?.data?.profile) {
      return Object.keys(answers).length > 0;
    }
    return Object.keys(answers).some((questionId) =>
      isQuestionChanged(questionId),
    );
  }, [answers, profileData, isQuestionChanged]);

  const handleAnswer = useCallback((questionId: string, value: any) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  }, []);

  // Called when an input signals it's finished - show interstitial or scroll to next
  const handleQuestionFinished = useCallback(
    (questionId: string, value: string | string[] | number) => {
      const question = quizQuestions.find((q) => q.id === questionId);
      if (!question) return;

      // Track quiz question completion
      const questionIndex = quizQuestions.findIndex((q) => q.id === questionId);
      tracking.quizQuestionAnswered(
        questionId,
        questionIndex,
        question.category ?? "",
        quizQuestions.length,
      );

      // Show interstitial if enabled and this question has one
      const hasInterstitial =
        interstitialsEnabled && question.interstitial && value;
      if (hasInterstitial) {
        setShownInterstitials((prev) => ({
          ...prev,
          [questionId]: { questionId, answer: value },
        }));
      }

      // Scroll to next unanswered question (with delay if interstitial shown)
      const q = quizQuestions[questionIndex + 1];
      if (q && !isQuestionAnswered(q.id)) {
        const ref = questionRefs.current[q.id];
        if (ref) {
          setTimeout(
            () => {
              ref.scrollIntoView({ behavior: "smooth", block: "center" });
            },
            hasInterstitial ? 3000 : 500,
          );
        }
        return;
      }
    },
    [isQuestionAnswered, interstitialsEnabled, quizQuestions],
  );

  const saveAnswers = useCallback(() => {
    console.log("saving answers", answers);
    setIsSaving(true);
    const { phone, instagram, ...quizAnswers } = answers;
    // First-party ad attribution captured from the landing URL — written once
    // server-side. utm_content carries the Meta ad id (per-ad DB CPA).
    const attr = getStoredFacebookAttribution();
    updateProfile.mutate(
      {
        quizAnswers: quizAnswers as any,
        phone: phone as string,
        instagram: instagram as string,
        ...(attr ? {
          signupAttribution: {
            utmSource: attr.utm_source, utmMedium: attr.utm_medium,
            utmCampaign: attr.utm_campaign, utmContent: attr.utm_content,
            utmTerm: attr.utm_term, fbclid: attr.fbclid,
            gclid: attr.gclid ?? null,
            landingPage: attr.landingPage ?? null,
          },
        } : {}),
      },
      {
        onSuccess: async () => {
          localStorage.removeItem("quizAnswers");
          tracking.quizCompleted();
          await invalidateProfile();
          navigate(
            resolvePostQuizDestination(
              searchParams.get("nav"),
              guidedBookingEnabled,
              getStoredAdEventType(),
            ),
          );
        },
        onError: (error) => {
          setIsSaving(false);
          setShowScanning(false);
          console.error("Failed to save profile:", error);
          toast.error(t("quiz.errors.saveFailed"));
        },
      },
    );
  }, [
    searchParams,
    answers,
    updateProfile,
    navigate,
    toast,
    invalidateProfile,
    guidedBookingEnabled,
    t,
  ]);

  // Called when the scanning animation finishes — trigger sign-in or save
  const handleScanComplete = useCallback(() => {
    if (!isSignedIn) {
      setShowScanning(false);
      openSignIn({
        asSignUp: true,
        onSuccess: () => {
          navigate("/quiz?saveAnswers");
        },
        // The redirect arm destroys this page, so the save has to be named as
        // a destination rather than left in the callback above.
        returnTo: "/quiz?saveAnswers",
      });
    } else {
      saveAnswers();
    }
  }, [isSignedIn, openSignIn, navigate, saveAnswers]);

  const handleSubmit = () => {
    if (!isSignedIn) {
      setShowScanning(true);
      return;
    }
    saveAnswers();
  };

  useEffect(() => {
    if (isInitialized && isSignedIn && shouldSaveAnswers.current) {
      console.log("Autosaving answers");
      shouldSaveAnswers.current = false;
      saveAnswers();
    }
  }, [
    saveAnswers,
    isSignedIn,
    shouldSaveAnswers,
    isInitialized,
  ]);

  if (showScanning) {
    return (
      <ScanningScreen
        onScanComplete={handleScanComplete}
      />
    );
  }

  if (updateProfile.isPending) {
    return (
      <div className="flex items-center justify-center bg-dark-bg min-h-[60vh]">
        <div className="text-center">
          <Loader className="mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">
            {t("quiz.updatingProfile")}
          </h2>
          <p className="text-dark-muted">{t("quiz.findingMatches")}</p>
        </div>
      </div>
    );
  }

  if ((isLoadingProfile || !isInitialized) && !isSaving) {
    return (
      <div className="flex items-center justify-center bg-dark-bg min-h-[60vh]">
        <div className="text-center">
          <Loader className="mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">
            {t("common.messages.loading")}
          </h2>
          <p className="text-dark-muted">{t("quiz.justMoment")}</p>
        </div>
      </div>
    );
  }

  if (isSaving) {
    return (
      <div className="flex items-center justify-center bg-dark-bg min-h-[60vh]">
        <div className="text-center">
          <Loader className="mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">{t("quiz.saving")}</h2>
          <p className="text-dark-muted">{t("quiz.justMoment")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 pb-24 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Questions Feed by Category */}
        {Object.entries(questionsByCategory).map(([category, questions]) => {
          const categoryAnsweredCount = questions.filter((q) =>
            isQuestionAnswered(q.id),
          ).length;
          const categoryTotal = questions.length;
          const allCategoryAnswered = categoryAnsweredCount === categoryTotal;

          // Filter to visible questions in this category
          const visibleQuestions = questions.filter(
            (q) =>
              visibleIndices.has(q.index) || q.index === previewQuestionIndex,
          );

          // Don't show category if no visible questions
          if (visibleQuestions.length === 0) return null;

          return (
            <div key={category} className="mb-8">
              {/* Category Header */}
              <div className="flex items-center gap-3 mb-4">
                <h3 className="text-lg font-semibold text-dark-text">
                  {category}
                </h3>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-dark-muted">
                    {categoryAnsweredCount}/{categoryTotal}
                  </span>
                  {allCategoryAnswered && (
                    <Check className="w-4 h-4 text-green-500" />
                  )}
                </div>
                <div className="flex-1 h-px bg-dark-border" />
              </div>

              {/* Questions in Category */}
              {visibleQuestions.map((q) => (
                <div key={q.id}>
                  <QuestionCard
                    question={q}
                    value={answers[q.id]}
                    onChange={(value) => handleAnswer(q.id, value)}
                    onFinished={(value) => handleQuestionFinished(q.id, value)}
                    cardRef={(el) => {
                      questionRefs.current[q.id] = el;
                    }}
                    isAnswered={isQuestionAnswered(q.id)}
                    isChanged={isQuestionChanged(q.id)}
                    isPreview={q.index === previewQuestionIndex}
                  />
                  {/* Inline interstitial after this question */}
                  {shownInterstitials[q.id] && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      transition={{ duration: 0.3, ease: "easeOut" }}
                      className="mb-4"
                    >
                      <Interstitial
                        questionId={shownInterstitials[q.id].questionId}
                        answer={shownInterstitials[q.id].answer}
                        onComplete={() => {}}
                      />
                    </motion.div>
                  )}
                </div>
              ))}
            </div>
          );
        })}

        {/* Submit Button */}
        {(hasUnsavedChanges || allRequiredAnswered || hasCompletedQuizBefore) && (
          <div
            className={cx(
              "sticky bottom-4 mt-8",
              profileData?.data?.profile ? "bottom-20 md:bottom-4" : "",
            )}
          >
            <motion.button
              onClick={handleSubmit}
              disabled={!allRequiredAnswered && !hasCompletedQuizBefore}
              className="w-full px-6 py-3 bottom-safe-margin text-lg font-semibold text-white shadow-lg disabled:cursor-not-allowed rounded-2xl relative overflow-hidden border-2 border-white/60 hover:border-white/100 dark:border-neutral-900/60 dark:hover:border-neutral-900/100 opacity-95 bg-white dark:bg-black hover:opacity-100 transition-opacity duration-300"
              style={{
                background: `linear-gradient(
                  to right,
                  rgb(236, 72, 153) 0%,
                  rgb(168, 85, 247) ${progress}%,
                  rgba(40, 40, 40, 0.9) ${progress}%,
                  rgba(30, 30, 30, 0.9) 100%
                )`,
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{
                opacity: 1,
                y: 0,
                background: `linear-gradient(
                  to right,
                  rgb(236, 72, 153) 0%,
                  rgb(168, 85, 247) ${progress}%,
                  rgba(40, 40, 40, 0.9) ${progress}%,
                  rgba(30, 30, 30, 0.9) 100%
                )`,
              }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              {allRequiredAnswered
                ? profileData?.data?.profile
                  ? t("quiz.saveChanges")
                  : t("quiz.completeProfile")
                : t("quiz.percentCompleted", { percent: progress })}
            </motion.button>
          </div>
        )}
      </div>
    </div>
  );
}
